/**
 * Fallback constants for the League Detail screen.
 * Used when the URL lacks a `leagueId` / country — not for populating real data.
 */

import type { LeagueStandingsScope } from '../../src/leagueDetail/types/leagueDetail.types'
import type { TeamMetricKey } from '../../src/leagueDetail/helpers/leagueTeamStats.helper'

export const FALLBACK_LEAGUE_ID = 'mock-premier-league'

/** flagcdn code for the fallback Premier League hero when no `countryCode` query is passed. */
export const FALLBACK_LEAGUE_COUNTRY_CODE = 'gb-eng'

/** Shown when we don't have a real `league.season` yet (e.g. mock / pre-fetch). */
export const FALLBACK_SEASON_LABEL = '2025/2026'

export type LeagueDetailNavTabKey =
  | 'table'
  | 'knockout'
  | 'fixtures'
  | 'playerStats'
  | 'teamStats'

export const LEAGUE_DETAIL_TAB_LABELS: Record<LeagueDetailNavTabKey, string> = {
  table: 'Table',
  knockout: 'Knockout',
  fixtures: 'Fixtures',
  playerStats: 'Player stats',
  teamStats: 'Team stats',
}

export const LEAGUE_DETAIL_TABLE_MODE_OPTIONS = [
  { key: 'full' as const, label: 'Full' },
  { key: 'form' as const, label: 'Form' },
]

/** Table scope when standings JSON includes `home` / `away` slices from API-Football. */
export const LEAGUE_STANDINGS_SCOPE_ITEMS: { key: LeagueStandingsScope; label: string }[] = [
  { key: 'all', label: 'Overall' },
  { key: 'home', label: 'Home' },
  { key: 'away', label: 'Away' },
]

export const LEAGUE_DETAIL_HIT_SLOP = { top: 12, bottom: 12, left: 12, right: 12 } as const

/** Cap OS font scaling on the dense standings grid so large display/font settings can't break the columns. */
export const DENSE_TABLE_MAX_FONT_SCALE = 1.2

/** Team-stats metric toggle (Scored / Conceded / Goal diff). */
export const METRIC_OPTIONS: { key: TeamMetricKey; label: string }[] = [
  { key: 'scored', label: 'Scored' },
  { key: 'conceded', label: 'Conceded' },
  { key: 'diff', label: 'Goal diff' },
]

export const METRIC_META: Record<
  TeamMetricKey,
  { label: string; hint: string; fewestFirst: boolean }
> = {
  scored: { label: 'Goals scored', hint: 'Most first', fewestFirst: false },
  conceded: { label: 'Goals conceded', hint: 'Fewest first', fewestFirst: true },
  diff: { label: 'Goal difference', hint: 'Best first', fewestFirst: false },
} as const
